import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Bell,
  CalendarCheck,
  CreditCard,
  Trophy,
  MessageSquare,
  AlertTriangle,
  UserCheck,
  Award,
  Heart,
  CheckCheck,
  Loader2,
  ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { useNotifications, type NotificationCategory } from "@/hooks/useNotifications";
import { ROUTES } from "@/lib/routes";
import { cn } from "@/lib/utils";

const categoryIcon: Record<NotificationCategory, JSX.Element> = {
  reservation: <CalendarCheck className="h-4 w-4 text-blue-500" />,
  class: <CalendarCheck className="h-4 w-4 text-green-500" />,
  no_show: <AlertTriangle className="h-4 w-4 text-orange-500" />,
  schedule_change: <AlertTriangle className="h-4 w-4 text-yellow-600" />,
  payment: <CreditCard className="h-4 w-4 text-emerald-500" />,
  invoice: <CreditCard className="h-4 w-4 text-orange-500" />,
  monthly_billing: <CreditCard className="h-4 w-4 text-orange-500" />,
  tournament: <Trophy className="h-4 w-4 text-purple-500" />,
  ranking: <Trophy className="h-4 w-4 text-amber-500" />,
  badge: <Award className="h-4 w-4 text-yellow-500" />,
  match_result: <Trophy className="h-4 w-4 text-blue-500" />,
  approval: <UserCheck className="h-4 w-4 text-green-600" />,
  invite: <MessageSquare className="h-4 w-4 text-primary" />,
  claim: <UserCheck className="h-4 w-4 text-blue-600" />,
  system: <Bell className="h-4 w-4 text-muted-foreground" />,
  social: <Heart className="h-4 w-4 text-pink-500" />,
};

export function NotificationBell({ className }: { className?: string }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { notifications, isLoading, unreadCount, markRead, markAllRead } =
    useNotifications({ limit: 8 });

  const handleItemClick = (id: string, actionUrl: string | null, isUnread: boolean) => {
    if (isUnread) markRead(id);
    setOpen(false);
    if (actionUrl) navigate(actionUrl);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn("relative rounded-full h-10 w-10", className)}
          aria-label={unreadCount > 0 ? `${unreadCount} notificações não lidas` : "Notificações"}
          title="Notificações"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-[360px] p-0">
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold">Notificações</p>
            {unreadCount > 0 && (
              <Badge variant="secondary" className="h-5 text-[10px]">
                {unreadCount} nova{unreadCount > 1 ? "s" : ""}
              </Badge>
            )}
          </div>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs gap-1"
              onClick={() => markAllRead()}
            >
              <CheckCheck className="h-3.5 w-3.5" />
              Marcar todas
            </Button>
          )}
        </div>

        {/* List */}
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <Bell className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm font-medium">Nenhuma notificação</p>
            <p className="text-xs text-muted-foreground">
              Você verá lembretes e alertas aqui
            </p>
          </div>
        ) : (
          <ScrollArea className="max-h-[420px]">
            <ul className="divide-y">
              {notifications.map((n) => {
                const isUnread = !n.read_at;
                return (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => handleItemClick(n.id, n.action_url, isUnread)}
                      className={cn(
                        "w-full text-left px-3 py-2.5 flex items-start gap-2.5 transition-colors hover:bg-muted/60",
                        isUnread && "bg-primary/5",
                      )}
                    >
                      <div className="mt-0.5 shrink-0">
                        {categoryIcon[n.category] ?? categoryIcon.system}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p
                            className={cn(
                              "text-sm truncate",
                              isUnread ? "font-semibold" : "font-medium",
                            )}
                          >
                            {n.title}
                          </p>
                          {isUnread && (
                            <span className="h-2 w-2 rounded-full bg-primary shrink-0" />
                          )}
                        </div>
                        {n.body && (
                          <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                            {n.body}
                          </p>
                        )}
                        <p className="text-[11px] text-muted-foreground mt-1">
                          {formatDistanceToNow(new Date(n.created_at), {
                            addSuffix: true,
                            locale: ptBR,
                          })}
                        </p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </ScrollArea>
        )}

        {/* Footer */}
        <div className="border-t p-2">
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="w-full justify-between text-xs"
            onClick={() => setOpen(false)}
          >
            <Link to={ROUTES.ATHLETE_NOTIFICACOES}>
              Ver todas as notificações
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
